import { SyntheticEvent } from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, TextField, Typography, useTheme } from "@mui/material";
import { DynamicQuestionAnswerInput } from "@interfaces/response";
import { DynamicQuestionCatalogItem } from "@interfaces/catalogs";
import CustomRadioGroup from "@components/CustomRadioGroup";

interface CustomDynamicQuestionFieldProps {
  question: DynamicQuestionCatalogItem;
  value: DynamicQuestionAnswerInput;
  onChange: (value: DynamicQuestionAnswerInput) => void;
  onBlur?: (event: SyntheticEvent) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | false;
}

// Renderiza una pregunta del catálogo real (catalogos/dynamic_question) según su "type":
// single_choice (radio), multiple_choice (checkboxes) o text (texto libre).
const CustomDynamicQuestionField = ({
  question,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  helperText,
}: CustomDynamicQuestionFieldProps) => {
  const theme = useTheme();
  const resolvedHelperText = helperText || question.help_text || undefined;

  if (question.type === "single_choice") {
    return (
      <CustomRadioGroup
        label={question.question}
        name={`dynamicAnswers.${question.code}`}
        required={question.required}
        value={(value as string) ?? ""}
        onChange={(event) => onChange((event.target as HTMLInputElement).value)}
        onBlur={onBlur}
        disabled={disabled}
        error={error}
        helperText={resolvedHelperText}
        options={(question.options ?? []).map((option) => ({
          label: option.label,
          value: String(option.value),
        }))}
      />
    );
  }

  if (question.type === "multiple_choice") {
    const selectedValues = Array.isArray(value) ? value : [];
    const maxReached = Boolean(question.max_selections) && selectedValues.length >= (question.max_selections as number);

    const handleToggle = (optionValue: string, checked: boolean) => {
      const nextValues = checked
        ? [...selectedValues, optionValue]
        : selectedValues.filter((selected) => selected !== optionValue);
      onChange(nextValues);
    };

    return (
      <Box>
        <Typography color={theme?.palette?.primary?.dark}>
          {question.question}
          {question.required ? " *" : ""}
        </Typography>
        <FormGroup>
          {(question.options ?? []).map((option) => {
            const optionValue = String(option.value);
            const isChecked = selectedValues.includes(optionValue);
            return (
              <FormControlLabel
                key={optionValue}
                control={
                  <Checkbox
                    name={`dynamicAnswers.${question.code}`}
                    checked={isChecked}
                    onChange={(event) => handleToggle(optionValue, event.target.checked)}
                    onBlur={onBlur}
                    disabled={disabled || (maxReached && !isChecked)}
                  />
                }
                label={option.label}
              />
            );
          })}
        </FormGroup>
        {resolvedHelperText && (
          <FormHelperText error={error}>{error ? helperText : resolvedHelperText}</FormHelperText>
        )}
      </Box>
    );
  }

  // "text"
  return (
    <TextField
      fullWidth
      label={question.question}
      name={`dynamicAnswers.${question.code}`}
      required={question.required}
      value={(value as string) ?? ""}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={error}
      helperText={resolvedHelperText}
      margin="normal"
      inputProps={question.max_length ? { maxLength: question.max_length } : undefined}
    />
  );
};

export default CustomDynamicQuestionField;
